import { useAuthActions } from "@convex-dev/auth/react";
import { convexQuery } from "@convex-dev/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "expo-router";
import React, { Fragment, useCallback, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { ScrollView, Text, View } from "react-native";
import { z } from "zod";
import ResponsiveContent from "@/elements/components/shared/responsive-content";
import { useSheet } from "@/elements/components/shared/sheet-provider";
import { AppBar } from "@/elements/components/ui/app-bar";
import { Button } from "@/elements/components/ui/button";
import { Input } from "@/elements/components/ui/input";
import { RadioInput } from "@/elements/components/ui/radio-input";
import { Select } from "@/elements/components/ui/select";
import { Textarea } from "@/elements/components/ui/textarea";
import { api } from "@/elements/components/utils/convex-api";
import { phoneNumberSchema } from "@/elements/components/utils/phoneNumberSchema";
import { SheetIds } from "@/elements/components/utils/sheet-ids";

const teachingExperienceSchema = z
  .object({
    countryOfResidenceId: z.string().min(1, "Country of residence is required"),
    languageIds: z
      .array(z.string())
      .min(1, "At least one language must be selected"),
    hasTeachingQualification: z.boolean(),
    teachingQualificationCountryOfIssue: z.string().optional(),
    detailsOfTeachingQualification: z.string().optional(),
    detailsOfEducationalDegree: z
      .string()
      .min(1, "Educational degree details are required"),
    teachingExperience: z.string().min(1, "Years of experience is required"),
    examBoardMarker: z.boolean(),
    detailsOfExamBoard: z.string().optional(),
    currentWorkplace: z.string().min(1, "Latest teaching position is required"),
    additionalInfo: z.string().optional(),
    phone: phoneNumberSchema,
  })
  .superRefine((data, ctx) => {
    if (data.examBoardMarker && !data.detailsOfExamBoard) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["detailsOfExamBoard"],
        message:
          "Exam board details are required when you are an exam board marker",
      });
    }
    if (data.hasTeachingQualification) {
      if (!data.teachingQualificationCountryOfIssue) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["teachingQualificationCountryOfIssue"],
          message: "Country of issue is required",
        });
      }
      if (!data.detailsOfTeachingQualification) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["detailsOfTeachingQualification"],
          message: "Teaching qualification details are required",
        });
      }
    }
  });

export type TeachingExperienceFormData = z.infer<
  typeof teachingExperienceSchema
>;

interface LanguageOption {
  value: string;
  label: string;
}

interface CountryOption {
  value: string;
  label: string;
}

function TeachingExperienceScreen() {
  const router = useRouter();

  const { signOut } = useAuthActions();

  const { open } = useSheet<any, any>(SheetIds.MULTI_SELECT);
  const { open: openSingleSelect } = useSheet<any, any>(SheetIds.SINGLE_SELECT);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { isValid },
  } = useForm<TeachingExperienceFormData>({
    resolver: zodResolver(teachingExperienceSchema),
    defaultValues: {
      hasTeachingQualification: true,
      examBoardMarker: false,
      languageIds: [],
    },
    mode: "onChange",
  });

  const watchedValues = watch();

  const { data: languages = [] } = useQuery({
    ...convexQuery(api.tutor.onboarding.queries.getAvailableLanguages, {}),
  });

  const { data: countries = [] } = useQuery({
    ...convexQuery(api.tutor.onboarding.queries.getAvailableCountries, {}),
  });

  const typedLanguages = languages as LanguageOption[];
  const typedCountries = countries as CountryOption[];

  const handleCountryOfResidenceOpen = useCallback(async () => {
    const result = await openSingleSelect({
      options: typedCountries,
      preSelectedOption: watchedValues.countryOfResidenceId,
    });
    if (result?.selectedOption) {
      setValue("countryOfResidenceId", result.selectedOption);
    }
  }, [
    openSingleSelect,
    watchedValues.countryOfResidenceId,
    typedCountries,
    setValue,
  ]);

  const handleLanguageOptionsOpen = useCallback(async () => {
    const result = await open({
      options: typedLanguages,
      preSelectedOptions: watchedValues.languageIds,
    });
    if (result?.selectedOptions) {
      setValue("languageIds", result.selectedOptions);
    }
  }, [open, watchedValues.languageIds, typedLanguages, setValue]);

  const onSubmit = useCallback(
    async (data: TeachingExperienceFormData) => {
      router.push(
        `/(app)/onboarding/(tutor)/curriculum-and-subjects?teachingExperience=${encodeURIComponent(
          JSON.stringify(data),
        )}`,
      );
    },
    [router],
  );

  useEffect(() => {
    (async () => {
      const phone = await AsyncStorage.getItem("phone");

      setValue("phone", phone ?? "");
    })();
  }, [setValue]);

  return (
    <View className="flex-1 bg-teachLoop-creamLight">
      <AppBar>
        <AppBar.Icon onPress={() => signOut()} />
        <AppBar.Title>Sign out</AppBar.Title>
      </AppBar>
      <ScrollView showsVerticalScrollIndicator={false}>
        <ResponsiveContent>
          <View className="mx-5">
            <View className="h-4" />
            <View className="gap-1">
              <Text className="font-zilla-slab-medium leading-[1.2] font-medium text-[28px] text-black">
                Tell us about your teaching experience.
              </Text>
              <Text className="font-geologica-regular font-normal text-xl text-black leading-[1.5]">
                Just a few details to get you started.
              </Text>
            </View>
            <View className="h-8" />
            <View className="gap-6">
              <View className="gap-4">
                <Text className="font-geologica-medium leading-6 font-medium text-[16px] text-teachLoop-textPrimary">
                  Country of residence
                </Text>
                <Controller
                  control={control}
                  name="countryOfResidenceId"
                  render={({ field: { value } }) => (
                    <Select size="md" onPress={handleCountryOfResidenceOpen}>
                      <Select.Label size="md">
                        {value
                          ? (typedCountries.find(
                              (country) => country.value === value,
                            )?.label as string)
                          : "Choose country"}
                      </Select.Label>
                      <Select.DownArrow />
                    </Select>
                  )}
                />
              </View>

              <View className="gap-4">
                <Text className="font-geologica-medium leading-6 font-medium text-[16px] text-teachLoop-textPrimary">
                  Mobile number
                </Text>

                <Controller
                  control={control}
                  name="phone"
                  render={({ field, fieldState: { error } }) => (
                    <Input
                      size="md"
                      className="py-[11]"
                      placeholder="+00 00000000"
                      error={!!error?.message}
                      {...field}
                    />
                  )}
                />
                <Text className="font-geologica-regular font-normal text-sm leading-[1.5] text-black">
                  This is only for Loopedin Admin to get in touch. It will never
                  be shared with parents.
                </Text>
              </View>

              <View className="gap-4">
                <Text className="font-geologica-medium leading-6 font-medium text-[16px] text-teachLoop-textPrimary">
                  Which languages do you speak fluently?
                </Text>
                <Controller
                  control={control}
                  name="languageIds"
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <Fragment>
                      <Select
                        size="md"
                        onPress={handleLanguageOptionsOpen}
                        error={!!error?.message}
                      >
                        <Select.Label size="md">
                          {value && value.length > 0
                            ? "Selected languages"
                            : "Select 1 or more languages"}
                        </Select.Label>
                        <Select.DownArrow />
                      </Select>
                      <Select.Results
                        selectedOptions={typedLanguages.filter((option) =>
                          value?.includes(option.value),
                        )}
                        onRemove={(languageValue) => {
                          const newValue =
                            value?.filter(
                              (option) => option !== languageValue,
                            ) || [];
                          onChange(newValue);
                        }}
                      />
                    </Fragment>
                  )}
                />
              </View>

              <View className="gap-4">
                <Text className="font-geologica-medium leading-6 font-medium text-[16px] text-teachLoop-textPrimary">
                  Do you have a teaching qualification?
                </Text>
                <View className="gap-4 flex-row items-center">
                  <View className="flex-1">
                    <Controller
                      control={control}
                      name="hasTeachingQualification"
                      render={({ field: { value, onChange } }) => (
                        <RadioInput onPress={() => onChange(true)}>
                          <RadioInput.Label size="md">Yes</RadioInput.Label>
                          <RadioInput.Icon checked={value} />
                        </RadioInput>
                      )}
                    />
                  </View>
                  <View className="flex-1">
                    <Controller
                      control={control}
                      name="hasTeachingQualification"
                      render={({ field: { value, onChange } }) => (
                        <RadioInput
                          onPress={() => {
                            onChange(false);
                            setValue(
                              "teachingQualificationCountryOfIssue",
                              undefined,
                            );
                            setValue(
                              "detailsOfTeachingQualification",
                              undefined,
                            );
                          }}
                        >
                          <RadioInput.Label size="md">No</RadioInput.Label>
                          <RadioInput.Icon checked={!value} />
                        </RadioInput>
                      )}
                    />
                  </View>
                </View>
              </View>

              {watchedValues.hasTeachingQualification && (
                <View className="gap-4">
                  <Text className="font-geologica-medium leading-6 font-medium text-[16px] text-teachLoop-textPrimary">
                    Country of Issue
                  </Text>
                  <Controller
                    control={control}
                    name="teachingQualificationCountryOfIssue"
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <Input
                        placeholder="Write the country that your teaching qualification is issued from."
                        multiline
                        className="text-sm"
                        value={value}
                        onChangeText={onChange}
                        error={!!error?.message}
                      />
                    )}
                  />
                </View>
              )}

              {watchedValues.hasTeachingQualification && (
                <View className="gap-4">
                  <Text className="font-geologica-medium leading-6 font-medium text-[16px] text-teachLoop-textPrimary">
                    Details of teaching qualification
                  </Text>
                  <Controller
                    control={control}
                    name="detailsOfTeachingQualification"
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <Input
                        placeholder="eg: PGCE (UK), Teaching diploma (UAE)"
                        multiline
                        className="text-sm"
                        value={value}
                        onChangeText={onChange}
                        error={!!error?.message}
                      />
                    )}
                  />
                </View>
              )}

              <View className="gap-4">
                <Text className="font-geologica-medium leading-6 font-medium text-[16px] text-teachLoop-textPrimary">
                  Details of your educational degree
                </Text>
                <Controller
                  control={control}
                  name="detailsOfEducationalDegree"
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <Input
                      placeholder="eg: BSc in Biology, BA in English Literature"
                      multiline
                      className="text-sm"
                      value={value}
                      onChangeText={onChange}
                      error={!!error?.message}
                    />
                  )}
                />
              </View>

              <View className="gap-4">
                <Text className="font-geologica-medium leading-6 font-medium text-[16px] text-teachLoop-textPrimary">
                  Years of teaching experience
                </Text>
                <Controller
                  control={control}
                  name="teachingExperience"
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <Input
                      placeholder="eg: 5+ teaching, 4+ tutoring"
                      multiline
                      value={value}
                      onChangeText={onChange}
                      error={!!error?.message}
                    />
                  )}
                />
              </View>

              <View className="gap-4">
                <Text className="font-geologica-medium leading-6 font-medium text-[16px] text-teachLoop-textPrimary">
                  Are you an exam board marker?
                </Text>
                <View className="gap-4 flex-row items-center">
                  <Controller
                    control={control}
                    name="examBoardMarker"
                    render={({ field: { value, onChange } }) => (
                      <Fragment>
                        <View className="flex-1">
                          <RadioInput onPress={() => onChange(true)}>
                            <RadioInput.Label size="md">Yes</RadioInput.Label>
                            <RadioInput.Icon checked={value} />
                          </RadioInput>
                        </View>

                        <View className="flex-1">
                          <RadioInput onPress={() => onChange(false)}>
                            <RadioInput.Label size="md">No</RadioInput.Label>
                            <RadioInput.Icon checked={!value} />
                          </RadioInput>
                        </View>
                      </Fragment>
                    )}
                  />
                </View>
              </View>

              {watchedValues.examBoardMarker && (
                <View className="gap-4">
                  <Text className="font-geologica-medium leading-6 font-medium text-[16px] text-teachLoop-textPrimary">
                    Details of the exam board
                  </Text>
                  <Controller
                    control={control}
                    name="detailsOfExamBoard"
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <Input
                        placeholder="eg: Board name, subject, curriculum"
                        multiline
                        className="text-sm"
                        value={value}
                        onChangeText={onChange}
                        error={!!error?.message}
                      />
                    )}
                  />
                </View>
              )}

              <View className="gap-4">
                <Text className="font-geologica-medium leading-6 font-medium text-[16px] text-teachLoop-textPrimary">
                  What is your latest teaching position?
                </Text>
                <Controller
                  control={control}
                  name="currentWorkplace"
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <Input
                      placeholder="eg: Year 6 Teacher, Secondary Science Tutor, Head of English, previous Head of Maths, SENCO Coordinator"
                      multiline
                      className="text-sm"
                      value={value}
                      onChangeText={onChange}
                      error={!!error?.message}
                    />
                  )}
                />
              </View>

              <View className="gap-4">
                <Text className="font-geologica-medium leading-6 font-medium text-[16px] text-teachLoop-textPrimary">
                  Additional info (Optional)
                </Text>
                <Controller
                  control={control}
                  name="additionalInfo"
                  render={({ field: { onChange, value } }) => (
                    <Textarea
                      placeholder={`Tell us what makes you a great tutor. You can include:
- Other services offered
- Wellbeing support
- SEND experience
- Parent support
- Courses or training
- LinkedIn or portfolio link`}
                      multiline
                      size="xl"
                      className="text-sm"
                      value={value}
                      onChangeText={onChange}
                    />
                  )}
                />
              </View>
            </View>
          </View>
          <View className="mx-5">
            <View className="h-8" />
            <Button
              size="md"
              className="w-full"
              intent="primary"
              onPress={handleSubmit(onSubmit)}
              disabled={!isValid}
            >
              <Button.Text size="md">Next</Button.Text>
            </Button>
            <View className="h-8" />
          </View>
        </ResponsiveContent>
      </ScrollView>
    </View>
  );
}

export default TeachingExperienceScreen;
